import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faGift, faCoffee, faGlassCheers, faLandmark, faTicketAlt } from '@fortawesome/free-solid-svg-icons';
import NewsletterSignUp from '../../common/Newsletter-sign';
import Footer from '../../Footer/Footer';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';
import { venposService } from '../../../services/venposService';

const grottoImage = 'https://BelvoirCastle.b-cdn.net/Christmas/Designer.webp';

const SANTA_LOCATION_ID = 978;
const SANTA_PAGE_ID = 'd771a85f-632e-4a25-8651-c3a60fb31a10';
const ALMOST_FULL_BELOW = 7;

const BOOKING_URL =
  `/booking-journey?locationId=${SANTA_LOCATION_ID}&pageId=${SANTA_PAGE_ID}`;

const heroImage =
  'https://www.belvoircastle.com/wp/wp-content/uploads/2026/07/Belvoir-Castle-Christmas-114-scaled.webp';

const SESSIONS = [
  { date: 'Saturday 28 November', iso: '2026-11-28', time: '5:00pm – 7:30pm', isNew: true },
  { date: 'Sunday 29 November', iso: '2026-11-29', time: '5:00pm – 7:30pm', isNew: true },
  { date: 'Saturday 5 December', iso: '2026-12-05', time: '5:00pm – 7:30pm' },
  { date: 'Sunday 6 December', iso: '2026-12-06', time: '5:00pm – 7:30pm' },
  { date: 'Saturday 12 December', iso: '2026-12-12', time: '5:00pm – 7:30pm' },
  { date: 'Sunday 13 December', iso: '2026-12-13', time: '5:00pm – 7:30pm' },
  { date: 'Saturday 19 December', iso: '2026-12-19', time: '5:00pm – 7:30pm' },
  { date: 'Sunday 20 December', iso: '2026-12-20', time: '5:00pm – 7:30pm' },
  { date: 'Monday 21 December', iso: '2026-12-21', time: '5:00pm – 7:30pm' },
  { date: 'Wednesday 23 December', iso: '2026-12-23', time: '5:00pm – 7:30pm' },
];

const availabilityLabel = (quantity) => {
  if (quantity == null || Number.isNaN(Number(quantity))) return null;
  if (Number(quantity) <= 0) return 'soldOut';
  if (Number(quantity) < ALMOST_FULL_BELOW) return 'almostFull';
  return null;
};

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  goldLt:    '#F5E8C8',
  bronze:    '#8B5E3C',
  crimson:   '#8B1A1A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) { height: clamp(520px, 82svh, 700px); }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.62) 32%,
    rgba(16,29,64,0.18) 60%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const heroTextShadow = '0 1px 10px rgba(10, 20, 40, 0.45)';
const heroGoldShadow = '0 1px 3px rgba(10, 20, 40, 0.75), 0 4px 16px rgba(10, 20, 40, 0.7)';

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  text-shadow: ${heroTextShadow};
  em { font-style: italic; color: ${C.gold}; text-shadow: ${heroGoldShadow}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  text-shadow: ${heroTextShadow};
  max-width: 38rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  margin-bottom: ${p => p.$tight ? '0.55rem' : '1.75rem'};
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 clamp(1.5rem, 3vw, 2.25rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201,150,60,0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  color: ${C.gold};
  font-size: 1.15rem;

  svg {
    width: 1.15rem;
    height: 1.15rem;
  }
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : C.muted};
`;

const Notice = styled.div`
  background: rgba(201,150,60,0.08);
  border-left: 4px solid ${C.gold};
  border-radius: 0 8px 8px 0;
  padding: 1rem 1.25rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${C.navy};
  font-weight: 600;
  margin-top: 1.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const ImgStrip = styled.div`
  border-radius: 14px;
  overflow: hidden;
  aspect-ratio: 3 / 4;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 30%;
    display: block;
  }
`;

const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${C.parchment};
  border: 1px solid rgba(201,150,60,0.2);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.28rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${C.navy};
  flex: 1;
  min-width: min(100%, 16rem);
`;

const MidCtaBtn = styled.a`
  flex-shrink: 0;
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const HeroBookBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.18s ease;
  &:hover { background: ${C.goldDk}; transform: translateY(-1px); }
`;

const SessionList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.75rem 1rem;
  margin-top: clamp(1.5rem, 3vw, 2rem);
`;

const SessionItem = styled.div`
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 1rem 1.15rem;
`;

const SessionTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.45rem;
`;

const SessionTag = styled.span`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #fff;
  background: ${p => p.$sold ? C.navy : C.gold};
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
`;

const SessionDate = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.05rem;
  font-weight: 700;
  color: ${C.navy};
  margin-bottom: 0.2rem;
`;

const SessionTime = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.85rem;
  color: ${C.muted};
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const CtaBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.32);
  letter-spacing: 0.06em;
`;

const SearchForSanta = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const [availability, setAvailability] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadAvailability = async () => {
      try {
        const itemsResponse = await venposService.getWebSaleItems(
          SANTA_LOCATION_ID,
          SANTA_PAGE_ID,
          SESSIONS[0].iso
        );
        const tickets = itemsResponse?.data?.webSaleItems || itemsResponse?.webSaleItems || [];
        const ticket = tickets.find((item) => item.webSaleItemId);
        if (!ticket) return;

        const selectedItems = [{
          pageType: 'WebPage',
          pageId: SANTA_PAGE_ID,
          webSaleItemId: ticket.webSaleItemId,
          quantity: 1,
          webSaleItemTypeID: ticket.webSaleItemTypeID,
        }];

        const results = await Promise.all(SESSIONS.map(async (session) => {
          try {
            const response = await venposService.getTimeSlots(
              SANTA_LOCATION_ID,
              SANTA_PAGE_ID,
              session.iso,
              selectedItems
            );
            const slots = response?.data?.timeSlots || [];
            const quantities = slots
              .map((slot) => Number(slot?.quantityAvailable))
              .filter((quantity) => !Number.isNaN(quantity));
            const quantity = quantities.length ? Math.min(...quantities) : null;
            return [session.iso, availabilityLabel(quantity)];
          } catch (error) {
            return [session.iso, null];
          }
        }));

        if (!cancelled) {
          setAvailability(Object.fromEntries(results));
        }
      } catch (error) {
        if (!cancelled) setAvailability({});
      }
    };

    loadAvailability();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>Search for Santa at Belvoir Castle | Christmas 2026 Late Afternoon Family Trail</title>
        <meta name="description" content="Search for Santa at Belvoir Castle — exclusive late afternoon family trail to find Father Christmas. Hot chocolate, biscuit and present for children; refreshments and bubbles for parents. Full-day Castle access included. 28 & 29 November, and 5, 6, 12, 13, 19, 20, 21 & 23 December 2026, 5pm–7:30pm. Limited to 30 per session." />
        <meta name="keywords" content="Search for Santa Belvoir Castle, late afternoon Santa trail, Christmas family trail Leicestershire, meet Santa December 2026, Father Christmas Belvoir, hot chocolate Santa trail, Christmas East Midlands families" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Belvoir Castle" />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/search-for-santa" />
        <link rel="alternate" type="text/html" href="https://www.belvoircastle.com/search-for-santa.html" title="Search for Santa — static AI-readable version" />
        <link rel="help" href="https://www.belvoircastle.com/llms.txt" title="Belvoir Castle AI-readable content index" />
        <meta property="og:title" content="Search for Santa at Belvoir Castle | Christmas 2026" />
        <meta property="og:description" content="Exclusive late afternoon family trail to find Father Christmas. 28 and 29 November, then selected December dates, 5pm–7:30pm. Full-day Castle access included. Limited spaces." />
        <meta property="og:image" content="https://BelvoirCastle.b-cdn.net/Christmas/Designer.webp" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/search-for-santa" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Search for Santa — Christmas 2026 at Belvoir Castle" />
        <meta name="twitter:description" content="Late afternoon family trail, 5pm–7:30pm on 28 and 29 November, then selected December dates. Hot chocolate, bubbles and a present from Santa." />
        <meta name="twitter:image" content="https://BelvoirCastle.b-cdn.net/Christmas/Designer.webp" />
        <meta name="twitter:site" content="@belvoircastle" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: 'Search for Santa at Belvoir Castle 2026',
            description: 'Exclusive late afternoon family trail through Belvoir Castle to find Father Christmas. Children enjoy hot chocolate, a biscuit and a present; parents get refreshments and bubbles. Full-day Castle access included. Limited to 30 per session.',
            image: 'https://BelvoirCastle.b-cdn.net/Christmas/Designer.webp',
            startDate: '2026-11-28T17:00:00',
            endDate: '2026-12-23T19:30:00',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            url: 'https://www.belvoircastle.com/christmas/search-for-santa',
            location: {
              '@type': 'Place',
              name: 'Belvoir Castle',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Belvoir Castle',
                addressLocality: 'Grantham',
                addressRegion: 'Leicestershire',
                postalCode: 'NG32 1PE',
                addressCountry: 'GB',
              },
            },
            organizer: { '@type': 'Organization', name: 'Belvoir Castle', url: 'https://www.belvoircastle.com' },
            offers: {
              '@type': 'Offer',
              url: 'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=d771a85f-632e-4a25-8651-c3a60fb31a10',
              priceCurrency: 'GBP',
              availability: 'https://schema.org/LimitedAvailability',
              description: 'Separate ticket including full-day Castle access — 28 and 29 November, then selected December dates, 5pm–7:30pm',
            },
          })}
        </script>
      </Helmet>

      <HeroWrap>
        <HeroImg src={heroImage} alt="Search for Santa at Belvoir Castle" />
        <HeroOverlay />
        <HeroContent>
          <HeroTitle>
            Search <em>for Santa</em>
          </HeroTitle>
          <HeroStrap>
            An exclusive late afternoon family trail through the Castle to find Father Christmas
          </HeroStrap>
          <BadgeRow $tight>
            <Pill>5pm – 7:30pm</Pill>
            <Pill>Hot Chocolate &amp; Bubbles</Pill>
            <Pill>Full-Day Castle Access</Pill>
          </BadgeRow>
          <BadgeRow>
            <Pill $fill>New dates</Pill>
            <Pill $fill>28–29 November</Pill>
            <Pill $fill>Selected December dates</Pill>
            <Pill $fill>Selling out fast</Pill>
          </BadgeRow>
          <HeroBookBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            Book Now
          </HeroBookBtn>
        </HeroContent>
      </HeroWrap>

      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCols $cols="1.1fr 0.9fr" $align="center">
            <div>
              <Kicker>The Experience</Kicker>
              <Divider />
              <SectionTitle>Find Santa<br /><em>in the Afternoon</em></SectionTitle>
              <SectionLead>
                Gather the family for an exclusive late afternoon adventure through Belvoir Castle. Follow a festive trail through the grand rooms, solve cheerful clues and meet friendly characters as you make your way to Father Christmas himself.
              </SectionLead>
              <SectionLead style={{ marginBottom: 0 }}>
                Children enjoy hot chocolate, a biscuit and a present from Santa. Parents are treated to refreshments and a glass of bubbles. Once the trail is complete, the Castle is yours for the whole day: explore Deck the Halls and the Mice Trail, all included with your ticket.
              </SectionLead>
              <Notice>
                Sessions run 5:00pm – 7:30pm on 28 and 29 November, then selected December dates. Spaces are limited to 30 per session — pre-booking is essential.
              </Notice>
              <MidCta style={{ marginTop: '1.5rem' }}>
                <MidCtaText>Secure your family's Search for Santa adventure</MidCtaText>
                <MidCtaBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">Book Now</MidCtaBtn>
              </MidCta>
            </div>
            <ImgStrip>
              <img src={grottoImage} alt="Father Christmas reading in the State Rooms at Belvoir Castle" />
            </ImgStrip>
          </TwoCols>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>What's Included</Kicker>
          <Divider />
          <SectionTitle>Everything Your <em>Family Gets</em></SectionTitle>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faSearch} /></FeatureIcon>
              <FeatureTitle>Late Afternoon Session</FeatureTitle>
              <FeatureBody>Sessions run from 5:00pm to 7:30pm — your family follows the clues through the grand rooms in the festive setting as the day draws to a close.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faGift} /></FeatureIcon>
              <FeatureTitle>Meet Father Christmas</FeatureTitle>
              <FeatureBody>The trail leads to Father Christmas himself — a private, joyful meeting in the Castle. Each child receives a special present to take home.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faCoffee} /></FeatureIcon>
              <FeatureTitle>For the Children</FeatureTitle>
              <FeatureBody>Every child enjoys hot chocolate, a biscuit and a present from Santa — the perfect festive treat after the adventure through the Castle rooms.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faGlassCheers} /></FeatureIcon>
              <FeatureTitle>For the Parents</FeatureTitle>
              <FeatureBody>Parents are treated to refreshments and a glass of bubbles — a warm festive welcome while the little ones enjoy their treats with Santa.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faLandmark} /></FeatureIcon>
              <FeatureTitle>Full-Day Castle Access</FeatureTitle>
              <FeatureBody>Your ticket includes full-day Castle access. Explore Deck the Halls, the Mice Trail and all the festive rooms as part of your visit.</FeatureBody>
            </FeatureCard>
            <FeatureCard $dark>
              <FeatureIcon><FontAwesomeIcon icon={faTicketAlt} /></FeatureIcon>
              <FeatureTitle $light>Separate Ticket</FeatureTitle>
              <FeatureBody $light>Search for Santa is a separate ticket from standard Castle entry — it includes its own full-day Castle access. 28 and 29 November, then selected December dates, with limited spaces per session.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>
        </Inner>
      </Section>

      <Section $bg={C.parchment} $border>
        <Inner>
          <Kicker>Dates &amp; Times</Kicker>
          <Divider />
          <SectionTitle>2026 <em>Sessions</em></SectionTitle>
          <SectionLead>
            All sessions start at 5:00pm and last 2 hours 30 minutes. Choose your date and book online — spaces are limited and new dates are selling fast.
          </SectionLead>
          <SessionList>
            {SESSIONS.map((session) => {
              const status = availability[session.iso];
              const showNew = session.isNew && status !== 'soldOut';
              const showAlmostFull = status === 'almostFull';
              const showSoldOut = status === 'soldOut';

              return (
              <SessionItem key={session.date}>
                {(showNew || showAlmostFull || showSoldOut) && (
                  <SessionTags>
                    {showNew && <SessionTag>New Date</SessionTag>}
                    {showAlmostFull && <SessionTag>Almost Full</SessionTag>}
                    {showSoldOut && <SessionTag $sold>Sold Out</SessionTag>}
                  </SessionTags>
                )}
                <SessionDate>{session.date}</SessionDate>
                <SessionTime>{session.time}</SessionTime>
              </SessionItem>
              );
            })}
          </SessionList>
          <MidCta style={{ marginTop: 'clamp(1.75rem, 3vw, 2.5rem)' }}>
            <MidCtaText>Ready to book your session?</MidCtaText>
            <MidCtaBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">Book Search for Santa</MidCtaBtn>
          </MidCta>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>Plan Your Visit</Kicker>
          <Divider />
          <SectionTitle>Essential <em>Information</em></SectionTitle>
          <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <InfoValue>28 &amp; 29 Nov, then<br />5, 6, 12, 13, 19, 20, 21 &amp; 23 Dec 2026</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Times</InfoLabel>
              <InfoValue>5:00pm – 7:30pm<br />(2 hrs 30 mins)</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>Belvoir Castle<br />Grantham, NG32 1PE</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Children</InfoLabel>
              <InfoValue>Hot chocolate,<br />biscuit &amp; present</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Parents</InfoLabel>
              <InfoValue>Refreshments<br />&amp; glass of bubbles</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Booking</InfoLabel>
              <InfoValue>Essential —<br />30 places per session</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      <div id="newsletter" style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Christmas 2026" />
      </div>

      <CtaSection>
        <CtaInner>
          <CtaTitle>
            Book Your <em>Search for Santa</em>
          </CtaTitle>
          <CtaBody>
            Spaces are limited to 30 per session and sell out fast. Secure your family's late afternoon adventure through the Castle — hot chocolate and a present for the children, refreshments and bubbles for the parents.
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              Book Now
            </CtaBtn>
            <CtaSecondary to="/christmas/your-festive-visit">
              Plan Your Visit
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            28 &amp; 29 November and selected December dates · 5:00pm – 7:30pm · Belvoir Castle, Grantham NG32 1PE
          </CtaMeta>
        </CtaInner>
      </CtaSection>
      <Footer />
    </Page>
  );
};

export default SearchForSanta;
